import React, { useState } from 'react';
import {
  getMatchHeader,
  getMatchLineups,
  getMatchPlayerStats,
  getAllFeedEvents,
  getAllFifaEvents,
} from '../services/serieaApi';

/* ─── Helpers ────────────────────────────────────────────────────── */
function downloadJson(data, filename) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = filename + '.json'; a.click();
  URL.revokeObjectURL(url);
}

function safe(v, fallback = '—') {
  return (v !== null && v !== undefined && v !== '') ? String(v) : fallback;
}

function fmtDate(d) {
  if (!d) return null;
  try {
    return new Date(d).toLocaleDateString('it-IT', {
      weekday: 'long', day: '2-digit', month: 'long',
      year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return String(d); }
}

/**
 * Cerca un valore in un oggetto provando più chiavi alternative.
 * Utile per gestire differenze tra versioni API / stagioni diverse.
 */
function pick(obj, ...keys) {
  for (const k of keys) {
    if (obj && obj[k] !== undefined && obj[k] !== null && obj[k] !== '') return obj[k];
  }
  return null;
}

/**
 * Cerca un valore in un array di stats provando più statsId alternativi.
 * es: lookupStat(index, 'goalAssist', 'assists', 'goal-assist')
 */
function lookupStat(index, ...ids) {
  for (const id of ids) {
    const v = index[id];
    if (v !== undefined && v !== null) return v;
  }
  return null;
}

/* ─── Event label per giocatore ──────────────────────────────────── */
const EVENT_LABELS = {
  'goal':             '⚽ Gol',
  'own-goal':         '⚽ Autogol',
  'owngoal':          '⚽ Autogol',
  'yellow-card':      '🟨 Ammonizione',
  'yellowcard':       '🟨 Ammonizione',
  'red-card':         '🟥 Espulsione',
  'redcard':          '🟥 Espulsione',
  'yellow-red-card':  '🟨🟥 Doppia ammonizione',
  'second-yellow':    '🟨🟥 Doppia ammonizione',
  'substitution-in':  '🔄 Entrato',
  'substitutionin':   '🔄 Entrato',
  'sub-on':           '🔄 Entrato',
  'substitution-out': '🔄 Uscito',
  'substitutionout':  '🔄 Uscito',
  'sub-off':          '🔄 Uscito',
  'penalty-scored':   '⚽ Rigore segnato',
  'penalty-missed':   '❌ Rigore fallito',
  'penalty-saved':    '🧤 Rigore parato',
};

function fmtEvent(ev) {
  if (!ev) return '';
  const type  = (ev.type || ev.eventType || ev.label || '').toLowerCase();
  const label = EVENT_LABELS[type] || ev.label || ev.type || type;
  const min   = ev.time != null ? `${ev.time}'` : ev.minute != null ? `${ev.minute}'` : '';
  const extra = (ev.additionalTime > 0) ? `+${ev.additionalTime}` : '';
  return `${label}${min ? ' al ' + min + extra : ''}`;
}

function fmtPlayer(p) {
  if (!p) return '';
  const name   = pick(p, 'displayName', 'shortName', 'shirtName', 'name') || '?';
  const role   = pick(p, 'roleLabel', 'role') || '';
  const num    = p.bibNumber ? `#${p.bibNumber}` : '';
  const cap    = p.isCaptain ? ' ©' : '';
  const gk     = p.isGoalkeeper ? ' [P]' : '';
  const evts   = Array.isArray(p.events) && p.events.length > 0
    ? ` (${p.events.map(fmtEvent).filter(Boolean).join(', ')})`
    : '';
  return `${num} ${name}${gk}${cap} — ${role}${evts}`.trim();
}

/* ─── KPI con alias multipli ─────────────────────────────────────── */
// Ogni entry: { label, ids: [...statsId alternativi in ordine di priorità] }
const KPI_DEFS = [
  { label: '⚽ Gol',                        ids: ['goals', 'goal', 'Goals'] },
  { label: '🅰️ Assist',                     ids: ['goalAssist', 'assists', 'assist', 'goal-assist'] },
  { label: '😞 Autogol',                    ids: ['ownGoals', 'own-goals', 'ownGoal', 'own-goal'] },
  { label: '⏱ Minuti giocati',              ids: ['minsPlayed', 'minutes', 'minutesPlayed', 'mins-played'] },
  { label: '🎯 Tiri in porta',              ids: ['ontargetScoringAtt', 'shots-on-goal', 'shotsOnGoal', 'shots-on-goal-successful'] },
  { label: '🏹 Tiri totali',                ids: ['totalScoringAtt', 'shots', 'totalShots'] },
  { label: '🔥 Grandi occasioni segnate',   ids: ['bigChanceScored', 'big-chance-scored'] },
  { label: '😬 Grandi occasioni mancate',   ids: ['bigChanceMissed', 'big-chance-missed', 'bigChanceMissed'] },
  { label: '📊 Expected Goals (xG)',        ids: ['expectedGoals', 'expected-goals', 'xg'] },
  { label: '🎯 Gol su rigore',              ids: ['attPenGoal', 'penaltyGoal', 'penalty-scored'] },
  { label: '❌ Rigore fallito',             ids: ['penaltyMissed', 'penalty-missed'] },
  { label: '🧤 Parate',                     ids: ['shotsSaved', 'saves', 'totalSaves'] },
  { label: '🧤 Clean sheet',               ids: ['cleanSheet', 'clean-sheet', 'cleansheet'] },
  { label: '📤 Passaggi totali',            ids: ['totalPass', 'pass-attempts', 'totalPasses', 'total-passes'] },
  { label: '✅ Passaggi riusciti',          ids: ['accuratePass', 'passes-successful', 'accurate-pass', 'accuratePasses'] },
  { label: '🎯 Precisione passaggi %',      ids: ['passing-accuracy-perc', 'passingAccuracyPerc', 'pass-accuracy'] },
  { label: '🔑 Passaggi terzo finale',      ids: ['successfulFinalThirdPasses', 'final-third-passes', 'finalThirdPasses'] },
  { label: '📏 Lanci lunghi riusciti',      ids: ['accurateLongBalls', 'accurate-long-balls'] },
  { label: '🧠 Passaggi intelligenti',      ids: ['smartPass', 'smart-pass'] },
  { label: '🤺 Duelli vinti',              ids: ['wonContest', 'duels-won', 'duelWon'] },
  { label: '🕹 Dribbling riusciti %',      ids: ['succDribblingPerc', 'dribbles', 'dribbling-perc'] },
  { label: '💪 Tackle vinti',              ids: ['wonTackle', 'tackles-successful', 'tacklesTotalWon'] },
  { label: '💪 Tackle vinti %',            ids: ['tackles-won-perc', 'tacklesWonPerc'] },
  { label: '✈️ Duelli aerei vinti %',      ids: ['aerial-duels-won-perc', 'aerialDuelsWonPerc', 'Aerials-Won-Perc', 'arials-won-perc'] },
  { label: '🛡 Intercettazioni',           ids: ['interception', 'interceptions'] },
  { label: '🧹 Respinte',                  ids: ['totalClearance', 'clearences', 'clearances'] },
  { label: '🔄 Recuperi palla',            ids: ['ballRecovery', 'ball-recovery', 'offRecovery'] },
  { label: '📦 Tocchi in area avversaria', ids: ['touchesInOppBox', 'touches-opponent-box', 'penAreaEntries'] },
  { label: '👣 Tocchi totali',             ids: ['touches'] },
  { label: '🟨 Ammonizione',               ids: ['yellowCard', 'yellow-cards', 'yellowCards'] },
  { label: '🟨🟥 Doppia ammonizione',     ids: ['secondYellow', 'second-yellow', 'secondYellowCard'] },
  { label: '🟥 Espulsione',               ids: ['redCard', 'red-cards', 'redCards', 'straightRedCard'] },
  { label: '🤕 Falli commessi',            ids: ['fouls'] },
  { label: '🤕 Falli subiti',             ids: ['wasFouled', 'fouls-suffered', 'foulsSuffered'] },
  { label: '🎯 Fallo da rigore',          ids: ['penaltyFoul', 'penalty-foul'] },
  { label: '📉 Palle perse',              ids: ['possLostAll', 'poss-lost-all', 'turnoverPass'] },
  { label: '💨 Sprint',                   ids: ['sprints'] },
  { label: '🏃 Distanza percorsa (m)',     ids: ['distance-covered', 'distanceCovered'] },
  { label: '⚡ Velocità max (km/h)',       ids: ['maximum-speed', 'maximumSpeed', 'maxSpeed'] },
];

/* ─── toNaturalLanguage ──────────────────────────────────────────── */
function toNaturalLanguage(data, type, matchData) {
  if (!data) return ['Nessun dato disponibile.'];

  try {

    /* ── HEADER ────────────────────────────────────────────── */
    if (type === 'header') {
      const lines = [];
      const status = pick(data, 'status', 'matchStatus', 'providerStatus');
      if (status) lines.push(`Stato: ${status}`);

      const date = fmtDate(pick(data, 'matchDateLocal', 'matchDateUtc', 'date', 'kickOffDate'));
      if (date) lines.push(`Data: ${date}`);

      const stadium = pick(data, 'stadiumName', 'venue', 'stadium');
      if (stadium) lines.push(`Stadio: ${typeof stadium === 'object' ? stadium.name : stadium}`);

      const city = pick(data, 'cityName', 'city');
      if (city) lines.push(`Città: ${city}`);

      const round = pick(data, 'roundName', 'groupName', 'matchSetName');
      if (round) lines.push(`Fase: ${round}`);

      const phase = pick(data, 'phase');
      if (phase && phase !== 'FULL_TIME') lines.push(`Fase di gioco: ${phase}`);

      const time = pick(data, 'time', 'minute');
      if (time != null && time !== '') lines.push(`Minuto: ${time}'`);

      // Squadra vincente — nome leggibile
      const winId = pick(data, 'winTeamId');
      if (winId) {
        const homeTeam = matchData?.home || matchData?.homeTeam;
        const awayTeam = matchData?.away || matchData?.awayTeam;
        const hId = homeTeam?.teamId || '';
        const aId = awayTeam?.teamId || '';
        const winShort = winId.split('::').pop();
        let winName = null;
        if (hId && (winId === hId || hId.includes(winShort))) {
          winName = pick(homeTeam, 'officialName', 'shortName', 'mediaName');
        } else if (aId && (winId === aId || aId.includes(winShort))) {
          winName = pick(awayTeam, 'officialName', 'shortName', 'mediaName');
        }
        lines.push(`🏆 Vincitore: ${winName || winShort}`);
      }

      const reason = pick(data, 'winReason');
      if (reason && reason !== 'RegularTime') lines.push(`Motivo: ${reason}`);

      const bc = data.editorial?.broadcasters;
      if (bc) {
        const channels = [bc.broadcasterNational1, bc.broadcasterNational2, bc.broadcasterNational3,
                          bc.broadcasterInternational1].filter(Boolean);
        if (channels.length) lines.push(`📺 Broadcaster: ${channels.join(', ')}`);
      }

      return lines.length ? lines : ['Nessuna informazione disponibile.'];
    }

    /* ── LINEUPS ───────────────────────────────────────────── */
    if (type === 'lineups') {
      const lines = [];

      const renderTeam = (team, label) => {
        if (!team) return;
        const name      = pick(team, 'officialName', 'shortName', 'mediaName', 'name') || label;
        const formation = pick(team, 'tacticalFormation', 'formation');

        // Titolari: può chiamarsi fielded, players, starters, starting11
        const fielded = Array.isArray(team.fielded)      ? team.fielded
          : Array.isArray(team.players)                  ? team.players.filter(p => p.status === 'Start' || p.formationPosition != null || p.isStarter)
          : Array.isArray(team.starters)                 ? team.starters
          : Array.isArray(team.starting11)               ? team.starting11
          : [];

        // Panchina: può chiamarsi substitutes, bench, subs, reserves
        const subs = Array.isArray(team.substitutes)     ? team.substitutes
          : Array.isArray(team.bench)                    ? team.bench
          : Array.isArray(team.subs)                     ? team.subs
          : Array.isArray(team.reserves)                 ? team.reserves
          : Array.isArray(team.players)                  ? team.players.filter(p => p.status === 'Sub' && !p.formationPosition)
          : [];

        // Staff / allenatore
        const staff = Array.isArray(team.staff) ? team.staff : [];
        const coach = staff.find(s =>
          s.roleLabel === 'Head Coach' || s.role === 101 ||
          (s.roleLabel || '').toLowerCase().includes('coach')
        );

        lines.push(`── ${name}${formation ? ` (${formation})` : ''} ──`);

        if (fielded.length) {
          lines.push(`Titolari (${fielded.length}):`);
          fielded.forEach(p => lines.push(`  ${fmtPlayer(p)}`));
        }
        if (subs.length) {
          lines.push(`Panchina (${subs.length}):`);
          subs.forEach(p => lines.push(`  ${fmtPlayer(p)}`));
        }
        if (coach) {
          const cname = pick(coach, 'shortName', 'displayName', 'name') || '?';
          lines.push(`Allenatore: ${cname}`);
        }
        if (!fielded.length && !subs.length) {
          lines.push('  Nessun dato formazione disponibile.');
        }
      };

      renderTeam(data.home, 'Casa');
      if (data.home && data.away) lines.push('');
      renderTeam(data.away, 'Ospite');

      return lines.length ? lines : ['Nessuna formazione disponibile.'];
    }

    /* ── PLAYER STATS ──────────────────────────────────────── */
    if (type === 'playerstats') {
      // Struttura attesa: { players: [{ player, team, stats: [{statsId, statsValue}] }] }
      // Fallback: array diretto di giocatori
      const players = Array.isArray(data.players) ? data.players
        : Array.isArray(data)                      ? data
        : [];

      if (!players.length) return ['Nessuna statistica disponibile.'];

      const lines = [`Statistiche giocatori (${players.length})`, ''];

      players.forEach(entry => {
        // Il giocatore può stare in entry.player o direttamente in entry
        const info  = entry.player || entry;
        const team  = entry.team   || {};
        // Le stats possono stare in entry.stats o entry.statistics
        const stats = Array.isArray(entry.stats)       ? entry.stats
          : Array.isArray(entry.statistics)            ? entry.statistics
          : [];

        const name     = pick(info, 'displayName', 'shortName', 'shirtName', 'name') || '?';
        const role     = pick(info, 'roleLabel', 'role') || '';
        const teamName = pick(team, 'shortName', 'officialName', 'name') || '';
        const bib      = info.bibNumber ? `#${info.bibNumber} ` : '';

        // Indice stats per lookup veloce, normalizza le chiavi
        const idx = {};
        stats.forEach(s => {
          const id = s.statsId || s.id || s.key || s.stat;
          if (id !== undefined) idx[id] = s.statsValue ?? s.value ?? s.val;
        });

        // Raccoglie KPI con valore > 0 usando alias multipli
        const relevant = KPI_DEFS
          .map(kpi => {
            const v = lookupStat(idx, ...kpi.ids);
            return (v !== null && parseFloat(v) > 0)
              ? `${kpi.label}: ${v}`
              : null;
          })
          .filter(Boolean);

        lines.push(`${bib}${name}${role ? ` (${role})` : ''}${teamName ? ` — ${teamName}` : ''}`);
        relevant.length
          ? relevant.forEach(r => lines.push(`  · ${r}`))
          : lines.push(`  · nessuna statistica rilevante`);
      });

      return lines;
    }

    /* ── MATCH FEED ────────────────────────────────────────── */
    if (type === 'feed') {
      const events = Array.isArray(data) ? data
        : Array.isArray(data.events)     ? data.events
        : Array.isArray(data.dataModel)  ? data.dataModel
        : [];

      if (!events.length) return ['Nessun evento disponibile.'];

      const counts = {};
      events.forEach(e => {
        const t = pick(e, 'eventName', 'typeName', 'type', 'typeId') || 'Sconosciuto';
        counts[t] = (counts[t] || 0) + 1;
      });
      const lines = [`Totale eventi: ${events.length}`, '', 'Distribuzione per tipo:'];
      Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 20)
        .forEach(([t, c]) => lines.push(`  ${t}: ${c}`));

      const goals = events.filter(e => {
        const tid  = e.typeId;
        const name = (pick(e, 'eventName', 'typeName', 'type') || '').toLowerCase();
        return tid === 16 || tid === '16' || name.includes('goal');
      });
      if (goals.length) {
        lines.push('', `⚽ Gol (${goals.length}):`);
        goals.forEach(g => {
          const min    = safe(pick(g, 'timeMin', 'minute', 'time'), '?');
          const player = safe(pick(g, 'playerName', 'player.name'), '?');
          const team   = safe(pick(g, 'teamName', 'team.name'), '?');
          lines.push(`  • ${min}' — ${player} (${team})`);
        });
      }
      return lines;
    }

    /* ── FIFA EVENTS ───────────────────────────────────────── */
    if (type === 'fifa') {
      const events = Array.isArray(data) ? data
        : Array.isArray(data.dataModel)  ? data.dataModel
        : Array.isArray(data.events)     ? data.events
        : [];

      if (!events.length) return ['Nessun evento FIFA disponibile.'];

      const counts = {};
      events.forEach(e => {
        const t = pick(e, 'type', 'eventType', 'typeName', 'typeId') || 'Sconosciuto';
        counts[t] = (counts[t] || 0) + 1;
      });
      const lines = [`Totale eventi FIFA: ${events.length}`, '', 'Distribuzione per tipo:'];
      Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 20)
        .forEach(([t, c]) => lines.push(`  ${t}: ${c}`));

      const NOTABLE = new Set(['Goal','Card','Substitution','PenaltyScored','PenaltyMissed','YellowCard','RedCard','goal','card','substitution']);
      const notable = events.filter(e => NOTABLE.has(pick(e, 'type', 'eventType') || ''));
      if (notable.length) {
        lines.push('', `Momenti chiave (${notable.length}):`);
        notable.slice(0, 15).forEach(e => {
          const min    = safe(pick(e, 'minute', 'timeMin', 'time'), '?');
          const player = safe(pick(e, 'playerName', 'player.name'), '?');
          const type   = pick(e, 'type', 'eventType') || '';
          lines.push(`  • ${min}' ${type} — ${player}`);
        });
      }
      return lines;
    }

  } catch (err) {
    return [`Errore nel parsing dei dati: ${err.message}`];
  }

  return ['Dati caricati.'];
}

/* ─── Fetch row ──────────────────────────────────────────────────── */
function FetchRow({ icon, label, filename, fetchFn, nlType, matchData }) {
  const [state,    setState]    = useState('idle');
  const [data,     setData]     = useState(null);
  const [err,      setErr]      = useState('');
  const [progress, setProgress] = useState('');
  const [open,     setOpen]     = useState(false);

  async function run() {
    setState('loading'); setData(null); setErr(''); setProgress(''); setOpen(false);
    try {
      const result = await fetchFn((page, total) => setProgress(`${page}/${total}`));
      setData(result);
      setState('done');
      setOpen(true);
    } catch (e) {
      setErr(e.message);
      setState('error');
    }
  }

  const lines = data ? toNaturalLanguage(data, nlType, matchData) : [];

  return (
    <div className={`fetch-row fetch-row-${state}`}>
      <div className="fetch-row-main">
        <span className="fetch-row-icon">{icon}</span>
        <span className="fetch-row-label">{label}</span>
        <div className="fetch-row-actions">
          {state === 'done' && (
            <>
              <button className="fetch-small-btn ghost" onClick={() => setOpen(o => !o)}>
                {open ? '▲' : '▼'}
              </button>
              <button className="fetch-small-btn ghost" onClick={() => downloadJson(data, filename)}>
                ⬇ JSON
              </button>
            </>
          )}
          <button
            className={`fetch-small-btn primary ${state === 'loading' ? 'loading' : ''}`}
            onClick={run}
            disabled={state === 'loading'}
          >
            {state === 'loading'
              ? <><span className="fetch-spinner" />{progress || '…'}</>
              : state === 'done' ? '↺' : 'Fetch'}
          </button>
        </div>
      </div>

      {state === 'error' && <div className="fetch-row-error">⚠ {err}</div>}

      {state === 'done' && open && lines.length > 0 && (
        <ul className="fetch-nl-list">
          {lines.map((line, i) =>
            line === ''
              ? <li key={i} className="fetch-nl-divider" />
              : <li key={i} className={
                  line.startsWith('  ') ? 'fetch-nl-sub' :
                  line.startsWith('──') ? 'fetch-nl-section' : ''
                }>{line.trim()}</li>
          )}
        </ul>
      )}
    </div>
  );
}

/* ─── Event Explorer Plugin ──────────────────────────────────────── */
export default function EventPlugin({ matchId, seasonId, baseName, match }) {
  const [open, setOpen] = useState(false);
  if (!matchId || !seasonId) return null;

  const rows = [
    { icon: '📋', label: 'Header partita',      nlType: 'header',      filename: `header_${baseName}`,      fetchFn: () => getMatchHeader(seasonId, matchId) },
    { icon: '👥', label: 'Formazioni',           nlType: 'lineups',     filename: `lineups_${baseName}`,     fetchFn: () => getMatchLineups(seasonId, matchId) },
    { icon: '📊', label: 'Player Stats',         nlType: 'playerstats', filename: `playerstats_${baseName}`, fetchFn: () => getMatchPlayerStats(seasonId, matchId) },
    { icon: '⚡', label: 'Match Feed',           nlType: 'feed',        filename: `feed_${baseName}`,        fetchFn: (cb) => getAllFeedEvents(seasonId, matchId, cb) },
    { icon: '🏅', label: 'FIFA Standard Events', nlType: 'fifa',        filename: `fifa_${baseName}`,        fetchFn: (cb) => getAllFifaEvents(matchId, cb) },
  ];

  return (
    <div className="mvp-plugin event-plugin">
      <div className="mvp-plugin-header">
        <span className="mvp-plugin-icon">⚡</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="mvp-plugin-title">Event Explorer</div>
          <div className="mvp-plugin-desc">Dati evento, formazioni e statistiche partita</div>
        </div>
        <button className="event-plugin-toggle" onClick={() => setOpen(o => !o)}>
          {open ? '▲ Chiudi' : '▼ Apri'}
        </button>
      </div>

      {open && (
        <div className="event-plugin-rows">
          {rows.map(row => <FetchRow key={row.label} {...row} matchData={match} />)}
        </div>
      )}
    </div>
  );
}
